import { Box, Button, HStack, Stack, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { ColumnDef, Table } from '@tanstack/react-table';
import { synchronizationAction } from 'actions';
import AmatriumSkeleton from 'components/AmatriumSkeleton';
import { PageContainer, PageHeader } from 'components/ds';
import Empty from 'components/Empty';
import ConfirmModal from 'components/Modal/ConfirmModal';
import ReactTable from 'components/ReactTable';
import StateText from 'components/StateText';
import { dateFormat } from 'helpers/date';
import { useAppDispatch, useAppSelector } from 'hooks';
import { ChevronLeft, CornerDownLeft, File, FolderOpen, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import IndeterminateCheckbox from './IndeterminateCheckbox';

interface Document {
  id: string;
  state: string;
  sync_by: string;
  is_folder: boolean;
  name: string;
  display_name: string;
  path: string;
  size: number;
  last_modified: string;
  last_modified_by: Record<string, any>;
}

const getProjectName = (url: string) => url.slice(url.lastIndexOf('/') + 1);

const DocumentList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {
    isOpen: isOpenSyncModal,
    onOpen: onOpenSyncModal,
    onClose: onCloseSyncModal,
  } = useDisclosure();
  const { isOpen: isOpenFolder, onOpen: onOpenFolder, onClose: onCloseFolder } = useDisclosure();
  const { documents, current, isLoading } = useAppSelector((state) => state.synchronization);
  const [tempSelectFolder, setTempSelectFolder] = useState({
    folder_name: '',
    folder_path: '',
  });

  const columns = useMemo<ColumnDef<Document>[]>(
    () => [
      {
        id: 'index',
        size: 50,
        header: '',
        cell: ({ row }) => {
          return (
            <div className=''>
              {row.original.is_folder ? (
                <IndeterminateCheckbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler(),
                    onClick: (e) => {
                      onSelectRow(e, row.original);
                    },
                  }}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'display_name',
        header: t('syncProject.documentName'),
        size: 500,
        cell: ({ row, table, cell }) => {
          return (
            <HStack>
              {row.original.is_folder ? <FolderOpen size={16} /> : <File size={16} />}
              <Text
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  _hover: {
                    textDecoration: row.original.is_folder ? 'underline' : 'none',
                  },
                }}
                onClick={(e) => handleOpenFolder(e, row.original, table)}
              >
                {cell.getValue() as string}
              </Text>
            </HStack>
          );
        },
      },
      {
        accessorKey: 'sync_by',
        size: 300,
        header: t('syncProject.syncBy'),
        cell: (info) => (
          <Text
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {info.getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: 'state',
        header: t('syncProject.state'),
        cell: (info) => {
          return (
            <Text
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {info.getValue() ? <StateText statusKey={info.getValue() as string} /> : ''}
            </Text>
          );
        },
      },
      {
        accessorKey: 'last_modified',
        header: t('syncProject.lastModified'),
        cell: (info) => {
          return (
            <>{info.getValue() ? <Text>{dateFormat(info.getValue() as string)}</Text> : null}</>
          );
        },
      },
      {
        accessorKey: 'size',
        header: t('syncProject.size_(KB)'),
        cell: (info) => {
          return (
            <>{info.getValue() ? <Text>{(info.getValue() as number).toFixed(2)}</Text> : null}</>
          );
        },
      },
    ],
    []
  );

  const handleOpenFolder = (
    e: React.MouseEvent<HTMLParagraphElement, MouseEvent>,
    row: Document,
    table: Table<Document>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!row.is_folder) return;

    if (table.getSelectedRowModel().flatRows.length) {
      setTempSelectFolder({
        folder_name: row.display_name,
        folder_path: row.path,
      });
      return onOpenFolder();
    } else {
      dispatch(
        synchronizationAction.updateCurrentFolder({
          folder_name: row.display_name,
          folder_path: row.path,
        })
      );

      dispatch(synchronizationAction.fetchDocumentListByProject(row.path));
    }
  };

  const resetProjectSelection = () => {
    dispatch(synchronizationAction.resetProject());
  };

  const fetchDocumentList = () => {
    dispatch(
      synchronizationAction.updateCurrentFolder({
        folder_name: tempSelectFolder.folder_name,
        folder_path: tempSelectFolder.folder_path,
      })
    );
    if (tempSelectFolder.folder_path) {
      dispatch(synchronizationAction.fetchDocumentListByProject(tempSelectFolder.folder_path));
    }
  };

  const onSyncDocument = async () => {
    dispatch(synchronizationAction.syncDocumentProcessing(true));
    dispatch(synchronizationAction.syncDocumentRequest());
  };

  const onSelectRow = (e: React.MouseEvent<HTMLInputElement, MouseEvent>, row: Document) => {
    e.stopPropagation();
    dispatch(synchronizationAction.updateCurrentSelectedFolders(row));
  };

  const getCurrentFolderName = (folderPath: string) => {
    const parts = folderPath.split('/').filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : '';
  };

  const handleBackBtn = () => {
    let getParentFolderPath = current.folder_path.slice(0, current.folder_path.lastIndexOf('/'));
    let getParentFolderName = getCurrentFolderName(getParentFolderPath);

    if (getParentFolderPath === '') {
      getParentFolderName = '';
      getParentFolderPath = '';
    }

    dispatch(
      synchronizationAction.updateCurrentFolder({
        folder_name: getParentFolderName,
        folder_path: getParentFolderPath,
      })
    );
    dispatch(synchronizationAction.fetchDocumentListByProject(getParentFolderPath));
  };

  useEffect(() => {
    if (!current.connection_id) {
      history.push('/synchronization');
    }
  }, []);

  return (
    <PageContainer>
      <Stack>
        {/* Page header — DS PageHeader (mockup .page-header): breadcrumb path title
            (left) + sync actions (right). */}
        <PageHeader
          title={
            <Tooltip
              label={`${getProjectName(current?.site_url || '') + current.folder_path}`}
              placement='bottom-start'
            >
              <Box
                as='span'
                sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {`${getProjectName(current?.site_url || '') + current.folder_path}`}
              </Box>
            </Tooltip>
          }
          action={
            <HStack spacing={3}>
              {current.folder_path === '' ? null : (
                <Button
                  leftIcon={<ChevronLeft size={16} />}
                  onClick={handleBackBtn}
                  variant='ghost'
                >
                  {t('common.back')}
                </Button>
              )}

              <Link to={'/sync-projects'}>
                <Button
                  leftIcon={<CornerDownLeft size={16} />}
                  onClick={resetProjectSelection}
                  variant='outline'
                >
                  {t('syncProject.projectList')}
                </Button>
              </Link>
              <Button
                isDisabled={isLoading}
                leftIcon={<RefreshCw size={16} />}
                onClick={onOpenSyncModal}
                variant='primary'
              >
                {t('syncProject.startToSync')}
              </Button>
            </HStack>
          }
        />

        <Box
          overflow='hidden'
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='lg'
        >
          {isLoading ? (
            <AmatriumSkeleton options={null} />
          ) : (
            <>
              {documents?.list?.length ? (
                <ReactTable columns={columns} data={documents.list} enableRowSelection />
              ) : (
                <HStack justifyContent={'center'}>
                  <Empty content={''} />
                </HStack>
              )}
            </>
          )}
        </Box>
      </Stack>

      <ConfirmModal
        isOpen={isOpenSyncModal}
        onClose={onCloseSyncModal}
        action={onSyncDocument}
        dataHeader={t('syncProject.confirmStartNewSync')}
        dataBody={t('syncProject.confirmStartNewSyncContent')}
        btnAction={t('common.sync')}
        color={'red'}
      />

      <ConfirmModal
        isOpen={isOpenFolder}
        onClose={onCloseFolder}
        action={fetchDocumentList}
        dataHeader={t('common.confirmToContinue')}
        dataBody={t('syncProject.confirmSelectedBeRemoved')}
        btnAction={t('common.continue')}
        color={'red'}
      />
    </PageContainer>
  );
};

export default DocumentList;
